import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, User, Eye, EyeOff, ShieldCheck, Crown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import adminApi, { ADMIN_TOKEN_KEY } from "@/lib/adminApi";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);

  // If already authenticated, redirect to /admin
  useEffect(() => {
    const t = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!t) return;
    (async () => {
      try {
        await adminApi.get("/admin-auth/me");
        navigate("/admin", { replace: true });
      } catch {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
      }
    })();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!username.trim() || !password) {
      toast.error("Please enter both username and password");
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.post("/admin-auth/login", {
        username: username.trim(),
        password,
      });
      localStorage.setItem(ADMIN_TOKEN_KEY, r.data.token);
      toast.success("Welcome to Admin Panel");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Login failed. Check your credentials.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden bg-[var(--surface-0)] text-[var(--text-1)] flex items-center justify-center px-4"
      data-testid="admin-login-page"
    >
      {/* Background ambient */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 10%, rgba(255,211,106,0.10) 0%, transparent 60%), radial-gradient(50% 40% at 80% 80%, rgba(124,58,237,0.10) 0%, transparent 60%)",
        }}
      />
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

      {/* Top bar */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-[420px]">
        {/* Brand */}
        <div className="flex flex-col items-center mb-6">
          <Logo size={64} glow />
          <div className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.20em] text-[#FFD36A]">
            <Crown size={11} />
            <span>Admin Control Panel</span>
          </div>
          <h1 className="text-2xl font-bold mt-1.5">SMART HUB</h1>
          <p className="text-[11px] text-[var(--text-3)] mt-1 text-center">
            Restricted access · Authorized personnel only
          </p>
        </div>

        {/* Card */}
        <form
          onSubmit={handleSubmit}
          className="relative rounded-2xl p-5 overflow-hidden"
          style={{
            background:
              "linear-gradient(180deg, var(--surface-1) 0%, var(--surface-2) 100%)",
            border: "1px solid var(--card3d-border)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.10), 0 24px 60px -20px rgba(0,0,0,0.40), 0 10px 30px -10px rgba(255,211,106,0.12)",
          }}
        >
          {/* Top sheen */}
          <div
            className="absolute top-0 left-4 right-4 h-px pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(255,211,106,0.55), transparent)",
            }}
          />

          <div className="space-y-4">
            {/* Username */}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-3)] mb-1.5 block">
                Username / Email
              </label>
              <div className="relative">
                <User
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]"
                />
                <Input
                  data-testid="admin-login-username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin@example.com"
                  disabled={busy}
                  className="pl-9 h-11 bg-[var(--surface-2)] border-[var(--card3d-border)] text-[var(--text-1)] placeholder:text-[var(--text-3)]"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-3)] mb-1.5 block">
                Password
              </label>
              <div className="relative">
                <Lock
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]"
                />
                <Input
                  data-testid="admin-login-password"
                  type={showPass ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={busy}
                  className="pl-9 pr-10 h-11 bg-[var(--surface-2)] border-[var(--card3d-border)] text-[var(--text-1)] placeholder:text-[var(--text-3)]"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((p) => !p)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md hover:bg-white/5 text-[var(--text-3)]"
                  data-testid="admin-login-toggle-pass"
                  tabIndex={-1}
                >
                  {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={busy || !username.trim() || !password}
              className="w-full h-11 font-bold text-black disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "linear-gradient(135deg, #FFD36A 0%, #F3BA2F 100%)",
                boxShadow:
                  "0 10px 24px -8px rgba(255,211,106,0.55), inset 0 1px 0 rgba(255,255,255,0.45)",
              }}
            >
              {busy ? (
                <>
                  <Loader2 size={15} className="animate-spin mr-2" /> Signing in…
                </>
              ) : (
                <>
                  <ShieldCheck size={15} className="mr-2" /> Sign in to Admin Panel
                </>
              )}
            </Button>

            {/* Footer notes */}
            <div className="pt-2 border-t border-[var(--card3d-border)] space-y-1.5">
              <div className="flex items-start gap-2 text-[10px] text-[var(--text-3)] leading-snug">
                <span className="w-1 h-1 rounded-full bg-[#22C55E] mt-1.5 shrink-0 shadow-[0_0_4px_#22C55E]" />
                <span>
                  Your session expires after 12 hours of inactivity for security.
                </span>
              </div>
              <div className="flex items-start gap-2 text-[10px] text-[var(--text-3)] leading-snug">
                <span className="w-1 h-1 rounded-full bg-[#F59E0B] mt-1.5 shrink-0 shadow-[0_0_4px_#F59E0B]" />
                <span>
                  This panel is for authorized administrators only. All actions are
                  logged.
                </span>
              </div>
            </div>
          </div>
        </form>

        {/* Bottom branding */}
        <div className="text-center mt-4 text-[10px] text-[var(--text-3)] uppercase tracking-[0.18em]">
          BSC Network · Chain 56 · v1.0
        </div>
      </div>
    </div>
  );
}
